import React, { Component } from 'react';
import Rest from 'tools/rest';
import { replace as _replace } from 'lodash';
import dayjs from 'dayjs';
import { withStyles } from '@material-ui/core/styles';
import { Table, DatePicker, Button, Typography, Statistic, Row, Col, Alert } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import Preloader from 'components/preloader';

const { Text } = Typography;

const MONTHS_RU = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

const moneyFmt = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const intFmt = new Intl.NumberFormat('ru-RU');

class AccrualsByTariff extends Component {
  state = {
    year: dayjs().year(),
    months: [],
    groups: [],
    grand_total: {},
    legal: { rows: [], totals: {} },
    payments: { rows: [], totals: {}, ratios: [] },
    loading: false,
    error: null,
  };

  componentWillUnmount() {
    document.title = _replace(document.title, ' | Начисления по тарифам', '');
  }

  componentDidMount() {
    document.title += ' | Начисления по тарифам';
    this.loadData();
  }

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.year !== this.state.year) {
      this.loadData();
    }
  }

  loadData = () => {
    this.setState({ loading: true, error: null });
    Rest.get('/api/v1/reports/accruals_by_tariff.json', { params: { year: this.state.year } })
      .then((response) => {
        const { months, groups, grand_total, legal, payments } = response.data;
        this.setState({
          months: months || [],
          groups: groups || [],
          grand_total: grand_total || {},
          legal: legal || { rows: [], totals: {} },
          payments: payments || { rows: [], totals: {}, ratios: [] },
        });
      })
      .catch((e) => {
        this.setState({ error: 'Не удалось загрузить отчёт. Попробуйте ещё раз.' });
        console.error(e);
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  downloadCsv = () => {
    window.open(`/api/v1/reports/accruals_by_tariff.csv?year=${this.state.year}`);
  };

  handleYearChange = (value) => {
    if (!value) return;
    this.setState({ year: value.year() });
  };

  formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return moneyFmt.format(value);
  };

  formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return `${(Number(value) * 100).toFixed(1)}%`;
  };

  buildDataSource() {
    const { groups, months, grand_total, legal, payments } = this.state;
    const rows = [];

    groups.forEach((group) => {
      rows.push({
        key: `g-${group.key}`,
        isGroup: true,
        name: group.title,
      });

      group.rows.forEach((row) => {
        rows.push({
          key: `r-${group.key}-${row.name}`,
          name: row.name,
          months: row.months || {},
        });
      });

      rows.push({
        key: `t-${group.key}`,
        isTotal: true,
        name: `ИТОГО ${group.title}`,
        months: group.totals || {},
      });
    });

    if (rows.length > 0) {
      rows.push({
        key: 'grand',
        isGrand: true,
        name: 'ИТОГО',
        months: grand_total || {},
      });
    }

    const paymentRows = (payments && payments.rows) || [];
    const paymentRatios = (payments && payments.ratios) || [];
    if (paymentRows.length > 0) {
      rows.push({
        key: 'pay-g',
        isGroup: true,
        isPayment: true,
        name: (payments && payments.title) || 'Оплаты',
      });

      rows.push({
        key: 'pay-t',
        isPaymentTotal: true,
        isPayment: true,
        name: 'ИТОГО ОПЛАТЫ',
        months: (payments && payments.totals) || {},
      });

      paymentRows.forEach((row) => {
        rows.push({
          key: `pay-r-${row.key || row.name}`,
          isPayment: true,
          name: row.name,
          months: row.months || {},
        });
      });

      paymentRatios.forEach((row) => {
        rows.push({
          key: `pay-ratio-${row.key}`,
          isPayment: true,
          isRatio: true,
          ratioFormat: row.format,
          name: row.name,
          months: row.months || {},
        });
      });
    }

    const legalRows = (legal && legal.rows) || [];
    if (legalRows.length > 0) {
      rows.push({
        key: 'legal-g',
        isGroup: true,
        isLegal: true,
        name: (legal && legal.title) || 'Юрлица (справочно)',
      });

      legalRows.forEach((row) => {
        rows.push({
          key: `legal-r-${row.name}`,
          isLegal: true,
          name: row.name,
          months: row.months || {},
        });
      });

      rows.push({
        key: 'legal-t',
        isTotal: true,
        isLegal: true,
        name: 'ИТОГО юрлица',
        months: (legal && legal.totals) || {},
      });
    }

    return { rows, months };
  }

  buildColumns(months) {
    const { classes } = this.props;
    const monthCount = months.length;

    const monthColumns = months.map((month) => {
      const date = dayjs(month);
      const title = `${MONTHS_RU[date.month()]} ${date.year()}`;

      return {
        title,
        align: 'center',
        children: [
          {
            title: 'ср. цена',
            dataIndex: ['months', month, 'price'],
            key: `${month}-price`,
            align: 'right',
            width: 80,
            render: (value, record) => {
              if (record.isGroup || record.isTotal || record.isGrand || record.isLegal || record.isPaymentTotal || record.isRatio) {
                return '';
              }
              if (record.isPayment) {
                const share = record.months && record.months[month] && record.months[month].share;
                return this.formatPercent(share);
              }
              return this.formatMoney(value);
            },
            onCell: (record) => {
              if (record.isGroup) return { colSpan: 0 };
              if (record.isTotal || record.isGrand || record.isPaymentTotal) return { className: classes.totalCell };
              return {};
            },
          },
          {
            title: 'кол-во',
            dataIndex: ['months', month, 'abonents'],
            key: `${month}-abonents`,
            align: 'right',
            width: 80,
            render: (value, record) => {
              if (record.isGroup || record.isPayment || record.isRatio) return '';
              return intFmt.format(value || 0);
            },
            onCell: (record) => {
              if (record.isGroup) return { colSpan: 0 };
              if (record.isTotal || record.isGrand || record.isPaymentTotal) return { className: classes.totalCell };
              return {};
            },
          },
          {
            title: 'итого',
            dataIndex: ['months', month, 'fee'],
            key: `${month}-fee`,
            align: 'right',
            width: 110,
            render: (value, record) => {
              if (record.isGroup) return '';
              if (record.ratioFormat === 'percent') {
                const share = record.months && record.months[month] && record.months[month].share;
                return this.formatPercent(share);
              }
              return this.formatMoney(value);
            },
            onCell: (record) => {
              if (record.isGroup) return { colSpan: 0 };
              if (record.isTotal || record.isGrand || record.isPaymentTotal) return { className: classes.totalCell };
              return {};
            },
          },
        ],
      };
    });

    return [
      {
        title: 'Название тарифа',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        width: 280,
        render: (value, record) => {
          if (record.isGroup) return <Text strong>{value}</Text>;
          if (record.isTotal || record.isGrand || record.isPaymentTotal) return <Text strong>{value}</Text>;
          return value;
        },
        onCell: (record) => {
          if (record.isGroup) {
            return { colSpan: 1 + monthCount * 3, className: classes.groupCell };
          }
          if (record.isTotal || record.isGrand || record.isPaymentTotal) {
            return { className: classes.totalCell, style: { background: '#e6f4ff' } };
          }
          return {};
        },
      },
      ...monthColumns,
    ];
  }

  yearFee() {
    const { grand_total } = this.state;
    return Object.values(grand_total).reduce((sum, cell) => sum + Number(cell.fee || 0), 0);
  }

  yearPayments() {
    const { payments } = this.state;
    return Object.values((payments && payments.totals) || {}).reduce((sum, cell) => sum + Number(cell.fee || 0), 0);
  }

  render() {
    const { classes } = this.props;
    const { year, loading, error } = this.state;
    const { rows, months } = this.buildDataSource();

    return (
      <React.Fragment>
        <PageHeader
          title="Начисления по тарифам"
          style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}
        >
          <Row gutter={16} align="middle">
            <Col>
              <DatePicker
                picker="year"
                allowClear={false}
                value={dayjs(`${year}-01-01`)}
                onChange={this.handleYearChange}
                className={classes.yearPicker}
              />
            </Col>
            <Col>
              <Button onClick={this.downloadCsv} disabled={loading || rows.length === 0}>
                Скачать CSV
              </Button>
            </Col>
            <Col>
              <Statistic
                title="Начислено за год"
                value={this.yearFee()}
                formatter={(value) => `${moneyFmt.format(value)} ₽`}
              />
            </Col>
            <Col>
              <Statistic
                title="Оплачено за год"
                value={this.yearPayments()}
                formatter={(value) => `${moneyFmt.format(value)} ₽`}
              />
            </Col>
          </Row>
        </PageHeader>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Начисления и оплаты физлиц из биллинга. Разовые продажи оборудования не входят. В блоке оплат — доля канала и отношение оплат к начислениям. Юрлица — справочно внизу."
        />

        {error && (
          <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} />
        )}

        <Preloader loading={loading}>
          <Table
            size="small"
            bordered
            pagination={false}
            rowKey="key"
            columns={this.buildColumns(months)}
            dataSource={rows}
            scroll={{ x: 260 + months.length * 270, y: 'calc(100vh - 320px)' }}
            locale={{ emptyText: 'Нет начислений за выбранный год' }}
            rowClassName={(record) => {
              if (record.isGroup) return classes.groupRow;
              if (record.isGrand || record.isTotal || record.isPaymentTotal) return classes.totalRow;
              return '';
            }}
          />
        </Preloader>
      </React.Fragment>
    );
  }
}

const styles = () => ({
  yearPicker: {
    width: 120,
  },
  groupRow: {
    backgroundColor: '#e6f4ff',
  },
  groupCell: {
    backgroundColor: '#e6f4ff',
    fontWeight: 600,
  },
  totalRow: {
    backgroundColor: '#e6f4ff',
    fontWeight: 600,
    '& td': {
      backgroundColor: '#e6f4ff !important',
    },
  },
  totalCell: {
    backgroundColor: '#e6f4ff !important',
    fontWeight: 600,
  },
});

export default withStyles(styles)(AccrualsByTariff);
